import type { FC } from 'react';
import { useState } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { adminService } from '@services/reportService';
import type { YearEndRolloverResult } from '@/types';
import toast from 'react-hot-toast';

interface YearEndRolloverModalProps {
  show: boolean;
  onHide: () => void;
}

const YearEndRolloverModal: FC<YearEndRolloverModalProps> = ({ show, onHide }) => {
  const [year, setYear] = useState<string>('');
  const [isExecuting, setIsExecuting] = useState(false);
  const [results, setResults] = useState<YearEndRolloverResult[] | null>(null);

  const handleExecute = async () => {
    if (!year || !/^\d{4}$/.test(year)) {
      toast.error('Please enter a valid 4-digit year');
      return;
    }

    setIsExecuting(true);
    setResults(null);

    try {
      const response = await adminService.executeYearEndRollover(year);

      if (response.status === 'success' && response.data) {
        setResults(response.data);
        toast.success('Year-end rollover completed successfully');
      } else {
        toast.error(response.message || 'Rollover failed');
      }
    } catch (error: any) {
      console.error('Error executing rollover:', error);
      toast.error(error.message || 'Failed to execute year-end rollover');
    } finally {
      setIsExecuting(false);
    }
  };

  const handleClose = () => {
    setYear('');
    setResults(null);
    onHide();
  };

  return (
    <Modal show={show} onHide={handleClose} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Year-End Leave Rollover</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>
          This will calculate year-end balances and create new year leave codes for all employees.
        </p>

        <Form.Group className="mb-3">
          <Form.Label>Year to rollover FROM:</Form.Label>
          <Form.Control
            type="number"
            placeholder="e.g., 2024"
            value={year}
            onChange={(e) => setYear(e.target.value)}
            disabled={isExecuting}
          />
          <Form.Text className="text-muted">
            Enter the year that is ending. New codes will be created for the following year.
          </Form.Text>
        </Form.Group>

        {results && results.length > 0 && (
          <div className="mt-3">
            <h6>Rollover Results:</h6>
            <div className="table-responsive">
              <table className="table table-sm table-striped">
                <thead>
                  <tr>
                    <th>Employee ID</th>
                    <th>PTO Balance</th>
                    <th>Comp Balance</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => (
                    <tr key={index}>
                      <td>{result.employee_id}</td>
                      <td>{result.pto_balance.toFixed(2)}</td>
                      <td>{result.comp_balance.toFixed(2)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {results && results.length === 0 && (
          <Alert variant="info" className="mt-3">
            No employees found or rollover already completed for this year.
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>
          Close
        </Button>
        <Button
          variant="warning"
          onClick={handleExecute}
          disabled={isExecuting || !year}
        >
          {isExecuting ? 'Executing...' : 'Execute Rollover'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default YearEndRolloverModal;
